import { Component, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

import { NSCommonConstants, NSCommons } from '@ruby/configs/common.constants';
import { NSRateConstants } from '@ruby/configs/rate-inquiry.constants';
import { IRateInquiry, IRateInquiryFavoriteItem, IRateSearch } from '@ruby/modules/rate-inquiry/models/rate-inquiry.interface';
import * as RateCoreActions from '@ruby/modules/rate-inquiry/store/actions/rate-core.actions';
import * as RateFormActions from '@ruby/modules/rate-inquiry/store/actions/rate-form.actions';
import * as fromRateCore from '@ruby/modules/rate-inquiry/store/reducers/rate-core.reducer';
import * as fromRateForm from '@ruby/modules/rate-inquiry/store/reducers/rate-form.reducer';
import * as RateFormSelectors from '@ruby/modules/rate-inquiry/store/selectors/rate-form.selectors';
import { EFavoriteApplicationType } from '@ruby/shared/enums/favorites.enums';
import { IStation } from '@ruby/shared/models/commons/station.interface';
import { IFavoriteData } from '@ruby/shared/models/favorites/favorites.interface';
import { ICarType } from '@ruby/shared/models/rate-inquiry/cartype.interface';
import { IStcc } from '@ruby/shared/models/rate-inquiry/stcc.interface';
import { FavoriteUtils } from '@ruby/shared/services/utils/favorite.utils';
import { UtilsService } from '@ruby/shared/services/utils/utils.service';

@Component({
  selector: 'ng-rate-form-container',
  templateUrl: './rate-form.container.html',
  styleUrls: ['./rate-form.container.scss']
})
export class RateFormContainerComponent implements OnInit {
  selectedFavorite?: IFavoriteData;
  store$: Observable<{ core: fromRateCore.State; form: fromRateForm.State }>;

  constructor(private store: Store<fromRateForm.State>, private favSvc: FavoriteUtils, private utilSvc: UtilsService) {
    this.store$ = this.store.select(RateFormSelectors.selectFormWithCoreCustomerState).pipe(
      tap((st: { core: fromRateCore.State; form: fromRateForm.State }) => {
        if (this.selectedFavorite) {
          this.selectedFavorite.content = st.core.searchList;
          this.setupSelectedFavorite();
        }
      })
    );
  }

  ngOnInit() {
    this.store.dispatch(RateFormActions.loadStates());
    this.store.dispatch(RateFormActions.loadCarOwners());
    this.store.dispatch(RateFormActions.setCarTypesItems({ data: [NSRateConstants.defaultCarType] }));
  }

  /**
   * Clear Dynamic Data
   *
   * @summary Called when an event is emitted from the component to clear all the fields
   * @returns void
   */
  clearDynamicData(): void {
    this.store.dispatch(RateFormActions.clearDynamicItems());
  }

  /**
   * Clear Stcc
   *
   * @summary Called to clear the STCC and car types lists
   * @returns void
   */
  clearStcc(): void {
    this.store.dispatch(RateFormActions.setCarTypesItems({ data: [] }));
    this.store.dispatch(RateFormActions.setStcc({ data: [] }));
  }

  /**
   * Get Car Types
   *
   * @summary Called when an event is emitted from the component
   * @param $stcc: string
   * @returns void
   */
  getCarTypes($stcc: string): void {
    this.store.dispatch(RateFormActions.loadCarTypes({ data: $stcc }));
  }

  /**
   * Set Car Types
   *
   * @summary Dispatch setCarTypeItems action
   * @param $carType: ICarType
   * @returns void
   */
  setCarTypes($carType: ICarType): void {
    this.store.dispatch(RateFormActions.setCarTypesItems({ data: [$carType] }));
  }

  /**
   * load Destination Stations
   *
   * @summary Called when an event is emitted from the component
   * @param $request: IStation
   * @returns void
   */
  loadDestinationStations($request: IStation): void {
    if ($request.station.length > 2) {
      this.store.dispatch(RateFormActions.loadDestinationStations({ data: $request }));
    } else {
      this.store.dispatch(RateFormActions.setDestinationStations({ data: [] }));
    }
  }

  /**
   * load Origin Stations
   *
   * @summary Called when an event is emitted from the component
   * @param $request: IStation
   * @returns void
   */
  loadOriginStations($request: IStation): void {
    if ($request.station.length > 2) {
      this.store.dispatch(RateFormActions.loadOriginStations({ data: $request }));
    } else {
      this.store.dispatch(RateFormActions.setOriginStations({ data: [] }));
    }
  }

  /**
   * Load STCC
   *
   * @summary Called when an event is emitted from the component
   * @param $stcc: string
   * @returns void
   */
  loadStcc($stcc: string): void {
    const request: IStcc = {};
    if ($stcc.match(NSCommonConstants.stccRegexNum)) {
      request.stccCode = {
        searchType: NSRateConstants.stccStartsWithCriteria,
        value: $stcc
      };
    }
    if ($stcc.match(NSCommonConstants.stccRegexAlpha)) {
      request.stccDescription = {
        searchType: NSRateConstants.stccContainsCriteria,
        value: $stcc
      };
    }
    this.store.dispatch(RateFormActions.loadStcc({ data: request }));
  }

  /**
   * Update Item
   *
   * @summary Dispatch the update item when is added or updated
   * @param $data: IRateInquiryFavoriteItem
   * @returns void
   */
  updateItem($data: IRateInquiryFavoriteItem): void {
    this.store.dispatch(RateCoreActions.setRateInquiryItem({ data: $data.inquiry }));
    if ($data.favorite) {
      this.selectedFavorite = {
        isFavorite: $data.favorite.isFavorite,
        content: $data.favorite.content,
        data: $data.favorite.data,
        name: $data.favorite.name,
        id: $data.favorite.id,
        applicationType: EFavoriteApplicationType.RATE_INQUIRY
      };
      this.setupSelectedFavorite();
    }
  }

  /**
   * Set Inquiry List
   *
   * @summary Dispatch setInquiryList action with the number of current inquiries
   * @param $num: number
   * @returns void
   */
  setInquiryList($num: number): void {
    this.store.dispatch(RateCoreActions.setInquiryList({ data: $num }));
  }

  /**
   * Set Display Form
   *
   * @summary Dispatch setDisplayForm action with the desired flag
   * @param $flag: boolean
   * @returns void
   */
  setDisplayForm($flag: boolean): void {
    this.store.dispatch(RateCoreActions.updateLayout({ data: $flag }));
  }

  /**
   * Clear Rate Results Data
   *
   * @summary Dispatch clearRateInquiry action
   * @returns void
   */
  clearRateResultsData(): void {
    this.store.dispatch(RateCoreActions.clearAllRateData());
    this.selectedFavorite = undefined;
  }

  /**
   * Clear Rate Search List
   *
   * @summary Dispatch clearRateSearchList & clearItem action
   * @returns void
   */
  clearRateSearchList(): void {
    this.store.dispatch(RateCoreActions.clearItem());
    this.store.dispatch(RateCoreActions.clearRateSearchList());
  }

  /**
   * Favorites Selected
   *
   * @summary This function set the list of rate inquiry search list
   * @param fav: IFavoriteData
   * @returns void
   */
  favoritesSelected(fav: IFavoriteData): void {
    const favContent = fav.content as Array<IRateSearch>;
    this.store.dispatch(RateCoreActions.setRateSearchList({ data: favContent }));
    this.store.dispatch(RateCoreActions.setInquiryList({ data: favContent.length }));
    this.selectedFavorite = { ...fav };
    this.setupSelectedFavorite();
  }

  /**
   * Setup Selected Favorite
   *
   * @summary Setup temp favorite when selected favorite exists
   * @returns void
   */
  setupSelectedFavorite(): void {
    if (this.selectedFavorite) {
      this.favSvc.setupTempFavorite(this.selectedFavorite);
    }
  }

  /**
   * Search
   *
   * @summary Dispatch searchInquiry action
   * @returns void
   */
  search(rate: IRateSearch): void {
    const inquiries: Array<IRateInquiry> = [];
    const rateInquiry: IRateInquiry = {
      originState: rate.originState.key,
      originCity: rate.originCity.key,
      destinationState: rate.destinationState.key,
      destinationCity: rate.destinationCity.key,
      carOwner: rate.carOwner.key,
      carType: rate.carType.key.carTypeCode,
      standardTransportationCommodityCode: rate.stcc.key,
      rule11: rate.rule11,
      shipmentConditionCode: rate.carType.key.shipmentCondition,
      carTypeDescription: rate.carType.value
    };

    if (rate.publishAuthority) {
      rateInquiry.publication = rate.publishAuthority;
    }

    if (rate.authorityNumber) {
      rateInquiry.authorizationNumber = Number(rate.authorityNumber);
    }

    inquiries.push(rateInquiry);
    this.store.dispatch(RateCoreActions.searchInquiry({ data: inquiries }));
    this.saveSearchAsHistoric(rate);
  }

  /**
   * Save Search As Historic
   *
   * @summary Save current search in history
   * @param data: IRateSearch
   * @returns void
   */
  saveSearchAsHistoric(data: IRateSearch): void {
    this.favSvc.saveHistoric({
      isFavorite: false,
      content: [data],
      applicationType: EFavoriteApplicationType.RATE_INQUIRY,
      name: `${ data.originState.key }, ${ data.originCity.key } to ${ data.destinationState.key },
        ${ data.destinationCity.key } - ${ data.stcc.key } - ${ NSCommons.formatNSDate(new Date()) }`
    });
  }

  /**
   * Set Display List
   *
   * @summary Dispatch setDisplayList action with the desired flag
   * @param $flag: boolean
   * @returns void
   */
  setDisplayList($flag: boolean): void {
    this.store.dispatch(RateCoreActions.setDisplayList({ data: $flag }));
  }

  /**
   * Set Favorite
   *
   * @summary Set the content of the current temp favorite
   * @param inquiry: Array<IRateSearch>
   * @returns void
   */
  setFavorite(inquiry: Array<IRateSearch>): void {
    const favorite: IFavoriteData = {
      isFavorite: false,
      content: inquiry,
      name: `${ inquiry[0].originState.key }, ${ inquiry[0].originCity.key } to
        ${ inquiry[0].destinationState.key }, ${ inquiry[0].destinationCity.key } - ${ inquiry[0].stcc.key } - ${ NSCommons.formatNSDate(new Date()) }`,
      applicationType: EFavoriteApplicationType.RATE_INQUIRY
    };
    this.selectedFavorite = { ...favorite };
    this.setupSelectedFavorite();
  }
}
